import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { ChevronUp, Crown, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { LEADERBOARD } from "@/constants/testIds";

export default function Leaderboard() {
  const [list, setList] = useState([]);
  const [voteFor, setVoteFor] = useState(null); // candidate object
  const [phase, setPhase] = useState("request"); // request | verify | done
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recentlyVoted, setRecentlyVoted] = useState(null);

  const refresh = async () => {
    try {
      const { data } = await api.get("/leaderboard");
      setList(data);
    } catch (e) {
      console.error("leaderboard fetch failed:", e);
    }
  };
  useEffect(() => { refresh(); }, []);

  const closeModal = () => {
    setVoteFor(null); setPhase("request"); setEmail(""); setName(""); setCode(""); setDevCode("");
  };

  const requestCode = async () => {
    if (!email || !name) { toast.error("Email + name required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/candidates/${voteFor.id}/vote/request`, { email, name, consent_marketing: true });
      setDevCode(data?.dev_code || "");
      setPhase("verify");
      toast.success("Verification code sent (check below).");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not send code");
    } finally { setBusy(false); }
  };

  const verifyVote = async () => {
    if (!code) { toast.error("Enter the verification code."); return; }
    setBusy(true);
    try {
      await api.post(`/candidates/${voteFor.id}/vote/verify`, { email, code });
      setRecentlyVoted(voteFor.id);
      toast.success(`Vote locked for ${voteFor.name} ⚡`);
      setTimeout(() => setRecentlyVoted(null), 1400);
      setPhase("done");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <section className="relative py-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <span className="label-gold">Viral Strike · Live Leaderboard</span>
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white mt-3 mb-3">
            Public <span className="gold-shimmer">Signal</span>.
          </h1>
          <p className="text-white/60 max-w-2xl">
            The most-upvoted Admiral candidate seizes <span className="text-[#C9A227] font-bold">$250 Boarding Credit + $500 EIGOO Ad Spend</span>. Every vote is email-verified to keep the signal pure.
          </p>

          <div className="mt-12 space-y-3">
            {list.length === 0 && (
              <div className="glass p-10 text-center border border-[#C9A227]/20">
                <p className="text-white/60 text-sm">No candidates have boarded yet. <Link to="/" className="text-[#C9A227]">Be the first.</Link></p>
              </div>
            )}
            {list.map((c, i) => (
              <div key={c.id}
                className={`glass border ${recentlyVoted === c.id ? "border-[#C9A227] vote-burst" : "border-[#C9A227]/15"} p-5 flex flex-wrap items-center gap-4 transition-colors`}
                data-testid={LEADERBOARD.card}>
                <div className="w-12 text-center">
                  {i === 0 ? <Crown className="text-[#C9A227] mx-auto"/> : <span className="text-2xl font-black text-white/30 tabular-nums">{i+1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <Link to={`/candidates/${c.id}`} className="block">
                    <h3 className="text-white text-lg md:text-xl font-black uppercase tracking-tight hover:text-[#C9A227] transition-colors">{c.name}</h3>
                  </Link>
                  <p className="text-white/55 text-xs mt-1 truncate">{c.niche}</p>
                  <div className="flex flex-wrap gap-3 mt-2 text-[10px] tracking-[0.25em] uppercase text-white/40">
                    {c.tiktok && <span>TikTok {c.tiktok}</span>}
                    {c.instagram && <span>IG {c.instagram}</span>}
                    {c.follower_count && <span className="text-[#C9A227]">{c.follower_count} followers</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-black gold-shimmer tabular-nums" data-testid={LEADERBOARD.upvoteCount}>▲ {c.upvotes}</div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-white/40">Upvotes</div>
                </div>
                <button onClick={() => setVoteFor(c)} className="btn-gold text-[10px]" data-testid={LEADERBOARD.upvoteBtn}>
                  Cast Upvote
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vote modal */}
      {voteFor && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={closeModal} data-testid={LEADERBOARD.voteModal}>
          <div className="glass border border-[#C9A227]/40 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <span className="label-gold">Viral Strike · Cast Vote</span>
            <h3 className="text-white text-2xl font-black uppercase tracking-tighter mt-2">{voteFor.name}</h3>

            {phase === "request" && (
              <div className="space-y-4 mt-6">
                <p className="text-white/55 text-sm">Email-verified votes only. We add you to the Sovereign Audience for future EIGOO drops.</p>
                <div>
                  <label className="label-gold">Your Name</label>
                  <input className="siren-input" value={name} onChange={(e) => setName(e.target.value)} data-testid={LEADERBOARD.voteName}/>
                </div>
                <div>
                  <label className="label-gold">Email</label>
                  <input type="email" className="siren-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={LEADERBOARD.voteEmail}/>
                </div>
                <button onClick={requestCode} disabled={busy} className="btn-gold w-full text-xs" data-testid={LEADERBOARD.voteRequestBtn}>
                  {busy ? "Sending…" : "Send Verification Code"}
                </button>
              </div>
            )}

            {phase === "verify" && (
              <div className="space-y-4 mt-6">
                <p className="text-white/55 text-sm">Enter the 6-digit verification code.</p>
                {devCode && (
                  <div className="p-3 border border-[#C9A227]/30 bg-black/40">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1">Dev Mode Code (no email wired)</div>
                    <div className="text-[#C9A227] font-black tracking-[0.4em] text-lg">{devCode}</div>
                  </div>
                )}
                <input className="siren-input tracking-[0.3em] text-center text-lg font-bold"
                  placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)}
                  data-testid={LEADERBOARD.voteCode}/>
                <button onClick={verifyVote} disabled={busy} className="btn-gold w-full text-xs" data-testid={LEADERBOARD.voteVerifyBtn}>
                  {busy ? "Verifying…" : "Confirm Upvote"}
                </button>
              </div>
            )}

            {phase === "done" && (
              <div className="space-y-4 mt-6 text-center">
                <ChevronUp className="text-[#C9A227] mx-auto" size={56}/>
                <p className="text-white text-lg font-bold">Vote secured for {voteFor.name}.</p>
                <button onClick={closeModal} className="btn-outline-gold text-xs">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
